import { ApolloClient, from, HttpLink, split } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { Kind, OperationTypeNode } from "graphql";
import { createClient } from "graphql-ws";
import { useMemo } from "react";

import { anonymousSessionStorage } from "../storage/instances";
import { cache } from "./apolloHelpers";
import { errorLink } from "./error";
import { graphqlRetryLink } from "./graphqlRetry";
import { retryLink } from "./retry";

const anonymousSessionLink = setContext(async (_, prevContext) => {
  const token = await anonymousSessionStorage.get("token");
  if (!token) {
    return prevContext;
  }
  const prevHeaders =
    prevContext?.headers != null && typeof prevContext.headers === "object"
      ? (prevContext.headers as Record<string, string>)
      : {};
  return {
    ...prevContext,
    headers: {
      ...prevHeaders,
      "X-Anonymous-Session-ID": token,
    },
  };
});

export const useApolloClient = (
  apolloPath: string,
  apolloWsPath: string,
  rethrowError?: (error: Error) => void,
  onAuthError?: () => Promise<void>,
  enableDevTools?: boolean,
) => {
  const httpLink = new HttpLink({
    uri: apolloPath,
    credentials: "include",
  });

  const wsClient = createClient({
    url: () => {
      if (typeof window === "undefined") {
        // In theory this should not happen
        throw new Error("window is not defined");
      }
      const { protocol, host } = window.location;
      return `${protocol === "http:" ? "ws" : "wss"}://${host}${apolloWsPath}`;
    },
  });

  const wsLink = new GraphQLWsLink(wsClient);

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === Kind.OPERATION_DEFINITION &&
        definition.operation === OperationTypeNode.SUBSCRIPTION
      );
    },
    wsLink,
    httpLink,
  );

  const errLink = useMemo(
    () => errorLink(rethrowError, onAuthError),
    [rethrowError, onAuthError],
  );

  return new ApolloClient({
    link: from([
      errLink,
      graphqlRetryLink,
      retryLink,
      anonymousSessionLink,
      splitLink,
    ]),
    connectToDevTools: enableDevTools ?? false,
    cache: cache,
  });
};
